import React from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { IBodyParts, IBodySurveyState } from '../store/bodySurvey.types'
import BodyZoomView from './BodyZoomView.content'
import {
  setEnabledZoom,
  setExaminationInProgress,
  setSelectedBodyPart,
  setSelectedBodyPartData,
  setZoomVisible,
  setZoomedBodyPart,
} from '../store/bodySurvey.actions'
import { showToast } from '../../../../../components/toast/store/toast.actions'
import { ToastColor } from '../../../../../components/toast/store/toast.constants'
import frontBodyParts from '../data/adultBodyFront.json'
import backBodyParts from '../data/adultBodyBack.json'
import FullBodyView from './FullBodyView.content'
import { LEFT_RIGHT_LABELS, getDocumentedLocations } from '../../../../../utilities/bodySurvey.util'

interface IBodyPartsContent {
  bodySurveyState: IBodySurveyState
}

const BodyParts: React.FC<IBodyPartsContent> = ({ bodySurveyState }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const {
    examValues,
    findings,
    examValuesPNNVs,
    __selectedBodyParts,
    __zoomedBodyPart,
    __selectedBodyPart,
    __chestLungMode,
    __enabledZoom,
    __setZoomVisible,
  } = bodySurveyState
  const combinedExamOptions = [...examValues, ...examValuesPNNVs]
  const allDocumentedLocations = getDocumentedLocations(combinedExamOptions, findings)

  const onZoom = (part: IBodyParts | null) => {
    dispatch(setZoomedBodyPart(part))
    dispatch(setEnabledZoom(!__enabledZoom))
    dispatch(setZoomVisible(false))
  }

  const onBodyPartSelection = (bodyVal: IBodyParts) => {
    const hasZoom = bodyVal.hasZoom
    dispatch(setZoomVisible(false))
    if (__selectedBodyPart?.id === bodyVal.id && hasZoom) {
      dispatch(setSelectedBodyPart(null))
      return
    }
    setTimeout(() => {
      dispatch(setSelectedBodyPart(bodyVal))
      if (hasZoom) {
        dispatch(setZoomVisible(true))
        dispatch(setSelectedBodyPartData([]))
      } else {
        dispatch(setZoomVisible(false))
      }
    }, 100)

    if (!hasZoom) {
      const isBodyPartAlreadyAdded = __selectedBodyParts.some((part) => part.id === bodyVal.id && !part.isExamined)
      const messageKey = isBodyPartAlreadyAdded ? 'bodyPartRemoved' : 'bodyPartAdded'
      dispatch(
        showToast({
          message: `${bodyVal.name} ${t(`additionalAssessments.notifications.${messageKey}`)}`,
          position: 'top',
          color: ToastColor.Info,
        })
      )
    }
    dispatch(setExaminationInProgress(false))
  }

  const renderFullBodyView = (title: string, svgData: any, leftRightLabels: any, showZoom: boolean) => (
    <FullBodyView
      title={title}
      svgData={svgData}
      viewBox='0 0 152 411'
      leftRightLabels={leftRightLabels}
      selectedPart={allDocumentedLocations}
      tempBodyParts={__selectedBodyParts}
      selectedBodyPart={__selectedBodyPart}
      zoomButtonVisible={__setZoomVisible}
      showZoom={!!__selectedBodyPart && showZoom}
      onBodyPartSelection={onBodyPartSelection}
      onZoomSelect={onZoom}
      setZoomButtonVisible={setZoomVisible}
    />
  )

  return (
    <div id='BodyPartsSection' className='body-section'>
      {!__enabledZoom ? (
        <>
          {renderFullBodyView(
            t('additionalAssessments.bodySurvey.front'),
            frontBodyParts,
            LEFT_RIGHT_LABELS.front,
            true
          )}
          {renderFullBodyView(t('additionalAssessments.bodySurvey.back'), backBodyParts, LEFT_RIGHT_LABELS.back, false)}
        </>
      ) : (
        <BodyZoomView
          savedBodyLocations={allDocumentedLocations}
          zoomedPart={__zoomedBodyPart}
          tempBodyParts={__selectedBodyParts}
          chestLungMode={__chestLungMode}
          onBodyPartSelection={onBodyPartSelection}
        />
      )}
    </div>
  )
}

export default BodyParts
